import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { HbrOption, HbrSelect } from '@harbor/elements-react-standalone';
import DataVisualizationComponent from '../Common/DataVisualizationComponent';
import { TileDefinition } from '../../types/dashboard';
import { TileRuntimeContext } from '../../support/tileRuntimeContext';

export type CurrentTimeFilter =
  | `range:${string}`
  | 'all'
  | 'previous_calendar_year'
  | 'current_fiscal_year'
  | 'last_quarter'
  | 'past_2_quarters'
  | 'past_3_quarters'
  | 'past_4_quarters'
  | 'past_5_quarters'
  | 'past_6_quarters';

const DEFAULT_CURRENT_TIME_FILTER: CurrentTimeFilter = 'all';

const CURRENT_TIME_FILTER_OPTIONS: Array<{ value: CurrentTimeFilter; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'previous_calendar_year', label: 'Previous Calendar Year' },
  { value: 'current_fiscal_year', label: 'Current Fiscal Year' },
  { value: 'last_quarter', label: 'Last Quarter' },
  { value: 'past_2_quarters', label: 'Past 2 Quarters' },
  { value: 'past_3_quarters', label: 'Past 3 Quarters' },
  { value: 'past_4_quarters', label: 'Past 4 Quarters' },
  { value: 'past_5_quarters', label: 'Past 5 Quarters' },
  { value: 'past_6_quarters', label: 'Past 6 Quarters' },
];

function getLeaderTileTimeFilterStorageKey(tileId: number) {
  return `leader_tile_time_filter:${tileId}`;
}

function getStoredLeaderTimeFilter(tileId: number): CurrentTimeFilter | null {
  try {
    const raw = window.localStorage.getItem(getLeaderTileTimeFilterStorageKey(tileId));
    return (raw as CurrentTimeFilter | null) ?? null;
  } catch {
    return null;
  }
}

function setStoredLeaderTimeFilter(tileId: number, value: CurrentTimeFilter) {
  try {
    window.localStorage.setItem(getLeaderTileTimeFilterStorageKey(tileId), value);
  } catch {
    // no-op
  }
}

interface LeaderTileProps {
  tile: TileDefinition;
  runtimeContext: TileRuntimeContext;
  setTileErrors: React.Dispatch<React.SetStateAction<Record<number, string | null>>>;
  err: string | null;

  suppressReduxInit?: any;
  customData?: any;
  settingsOverride?: any;
  queriesOverride?: any[];
  transformationsOverride?: any[];

  currentTimeFilterOverride?: CurrentTimeFilter;
  onCurrentTimeFilterOverrideChange?: (value: CurrentTimeFilter) => void;
  refetchTrigger?: string;
  /** Replaces the tile's configured name in its header (e.g. to reflect a page-level time period). */
  titleOverride?: string;
}

function getInitialCurrentTimeFilter(
  tile: TileDefinition,
  runtimeContext: TileRuntimeContext,
  currentTimeFilterOverride?: CurrentTimeFilter
): CurrentTimeFilter {
  if (currentTimeFilterOverride) return currentTimeFilterOverride;

  const stored = getStoredLeaderTimeFilter(tile.id);
  if (stored) return stored;

  const runtimeValue = runtimeContext?.current_time_filter as CurrentTimeFilter | undefined;
  if (runtimeValue) return runtimeValue;

  const defaultDateRange = tile?.settings?.defaultDateRange as CurrentTimeFilter | undefined;
  if (defaultDateRange) return defaultDateRange;

  return DEFAULT_CURRENT_TIME_FILTER;
}

const LeaderTile: React.FC<LeaderTileProps> = ({
                                                 tile,
                                                 runtimeContext,
                                                 setTileErrors,
                                                 err,
                                                 suppressReduxInit,
                                                 customData,
                                                 settingsOverride,
                                                 queriesOverride,
                                                 transformationsOverride,
                                                 currentTimeFilterOverride,
                                                 onCurrentTimeFilterOverrideChange,
                                                 refetchTrigger,
                                                 titleOverride,
                                               }) => {
  const isOverrideMode = currentTimeFilterOverride != null;

  const [currentTimeFilter, setCurrentTimeFilter] = useState<CurrentTimeFilter>(() =>
    getInitialCurrentTimeFilter(tile, runtimeContext, currentTimeFilterOverride)
  );

  useEffect(() => {
    if (isOverrideMode) return;

    const nextFilter = getInitialCurrentTimeFilter(tile, runtimeContext, undefined);
    setCurrentTimeFilter((prev) => (prev === nextFilter ? prev : nextFilter));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isOverrideMode,
    tile.id,
    runtimeContext?.current_time_filter,
    tile?.settings?.defaultDateRange,
  ]);

  const handleTileError = useCallback(
    (e: string | null) => {
      setTileErrors((prev) => {
        const nextValue = e ?? null;
        const currentValue = prev[tile.id] ?? null;

        if (currentValue === nextValue) return prev;

        return {
          ...prev,
          [tile.id]: nextValue,
        };
      });
    },
    [setTileErrors, tile.id]
  );

  const effectiveCurrentTimeFilter = currentTimeFilterOverride ?? currentTimeFilter;

  const mergedRuntimeContext = useMemo(
    () => ({
      ...(runtimeContext ?? {}),
      current_time_filter: effectiveCurrentTimeFilter,
    }),
    [runtimeContext, effectiveCurrentTimeFilter]
  );

  const mergedSettingsOverride = useMemo(
    () => ({
      ...(settingsOverride ?? {}),
    }),
    [settingsOverride]
  );

  const effectiveQueries = useMemo(
    () => (queriesOverride ?? tile.queries ?? []),
    [queriesOverride, tile.queries]
  );

  const shouldShowTimeFilter = useMemo(
    () => effectiveQueries.some((query: any) => query.enableDateFilter),
    [effectiveQueries]
  );

  // A page-level filter (override) is the single Time Period for every tile on the page, so the card's own
  // dropdown is hidden. It stays only where something is listening for its changes (the tile-builder preview).
  const showCardTimeFilter =
    shouldShowTimeFilter &&
    !String(currentTimeFilterOverride ?? '').startsWith('range:') &&
    (!isOverrideMode || !!onCurrentTimeFilterOverrideChange);

  const handleCurrentTimeFilterChange = (e: any) => {
    const nextValue = (e?.target?.value || DEFAULT_CURRENT_TIME_FILTER) as CurrentTimeFilter;

    if (isOverrideMode) {
      onCurrentTimeFilterOverrideChange?.(nextValue);
      return;
    }

    setCurrentTimeFilter((prev) => (prev === nextValue ? prev : nextValue));
    setStoredLeaderTimeFilter(tile.id, nextValue);
  };

  const effectiveRefetchTrigger =
    refetchTrigger ?? `leader:${tile.id}:${effectiveCurrentTimeFilter}`;

  return (
    <div
      key={tile.id}
      // Card chrome matches the shared dashboard aesthetic (see CampaignChartCard `DASH`): 16px radius,
      // soft card shadow, divided header/body. Values are inlined (the `--cb-*` tokens are scoped to the
      // Campaign Builder subtree and don't resolve here).
      style={{
        minHeight: 0,
        minWidth: 0,
        display: 'flex',
        height: '100%',
        width: '100%',
        flexDirection: 'column',
        overflow: 'hidden',
        position: 'relative',
        isolation: 'isolate',
        boxSizing: 'border-box',
        background: '#ffffff',
        border: '1px solid #e5e7eb',
        borderRadius: 16,
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
      }}
    >
      <div
        style={{
          flex: '0 0 auto',
          minHeight: 20,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          padding: '12px 16px',
          borderBottom: '1px solid #e5e7eb',
          minWidth: 0,
          position: 'relative',
          overflow: 'visible',
          zIndex: 1,
        }}
      >
        <div
          style={{
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            flex: '1 1 auto',
            fontSize: '0.875rem',
            fontWeight: 700,
            color: '#1a2332',
          }}
          title={titleOverride ?? tile.name}
        >
          {titleOverride ?? tile.name}
        </div>

        {showCardTimeFilter && (
          <div
            style={{
              flex: '0 0 auto',
              width: 220,
              minWidth: 220,
              marginLeft: 'auto',
              position: 'relative',
              overflow: 'visible',
              zIndex: 2,
            }}
          >
            <HbrSelect
              value={effectiveCurrentTimeFilter}
              onHbr-change={handleCurrentTimeFilterChange}
            >
              {CURRENT_TIME_FILTER_OPTIONS.map((option) => (
                <HbrOption key={option.value} value={option.value}>
                  {option.label}
                </HbrOption>
              ))}
            </HbrSelect>
          </div>
        )}
      </div>

      <div
        style={{
          flex: '1 1 auto',
          width: '100%',
          minHeight: 0,
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          position: 'relative',
          padding: '12px 14px',
          boxSizing: 'border-box',
        }}
      >
        {err ? (
          <div
            style={{
              padding: 8,
              color: 'var(--hbr-color-danger)',
              overflow: 'auto',
            }}
          >
            {err}
          </div>
        ) : (
          <div
            style={{
              width: '100%',
              height: '100%',
              minWidth: 0,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              position: 'relative',
              boxSizing: 'border-box',
            }}
          >
            <DataVisualizationComponent
              tileDefinition={tile}
              runtimeContext={mergedRuntimeContext}
              currentTimeFilter={effectiveCurrentTimeFilter}
              setTileError={handleTileError}
              suppressReduxInit={suppressReduxInit}
              customData={customData}
              settingsOverride={mergedSettingsOverride}
              queriesOverride={queriesOverride}
              transformationsOverride={transformationsOverride}
              refetchTrigger={effectiveRefetchTrigger}
              displayContext="leader"
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default LeaderTile;